import { useState, useEffect } from 'react';
import AccessibleModal from './AccessibleModal';
import { useTranslation } from 'react-i18next';
import ThemeSwitcher from './ThemeSwitcher';
import WebhooksPanel from './WebhooksPanel';
import { useAuth } from '../contexts/AuthContext';
import IntegrationsCatalogView from './IntegrationsCatalogView';
import BillingView from './BillingView';
import ReplicaAgentTab from './settings/ReplicaAgentTab';

// ── Persistence helpers ──────────────────────────────────────────────
const SETTINGS_KEY = 'humanreplica_settings';

interface SettingsState {
    emailNotifications: boolean;
    dataCollection: boolean;
    language: string;
}

function loadSettings(): SettingsState {
    try {
        const raw = localStorage.getItem(SETTINGS_KEY);
        if (raw) return JSON.parse(raw);
    } catch { /* ignore */ }
    return { emailNotifications: false, dataCollection: true, language: 'en' };
}

function saveSettings(s: SettingsState) {
    localStorage.setItem(SETTINGS_KEY, JSON.stringify(s));
}

interface SettingsViewProps {
    onNavigate?: (view: string) => void;
}

// ── Component ────────────────────────────────────────────────────────
export default function SettingsView({ onNavigate }: SettingsViewProps = {}) {
    const { user } = useAuth();
    const { i18n } = useTranslation('dashboard');

    const [settings, setSettings] = useState<SettingsState>(loadSettings);
    const [activeTab, setActiveTab] = useState<'general' | 'integrations' | 'billing' | 'replica-agent'>('general');
    const [show2FAModal, setShow2FAModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [exportingData, setExportingData] = useState(false);
    const [exportDone, setExportDone] = useState(false);
    const [saved, setSaved] = useState(false);

    // Persist whenever settings change
    useEffect(() => { saveSettings(settings); }, [settings]);

    // Flash a "saved" badge briefly
    const flashSaved = () => { setSaved(true); setTimeout(() => setSaved(false), 1500); };

    const toggle = (key: keyof Pick<SettingsState, 'emailNotifications' | 'dataCollection'>) => {
        setSettings(prev => ({ ...prev, [key]: !prev[key] }));
        flashSaved();
    };

    const handleLanguageChange = (lang: string) => {
        setSettings(prev => ({ ...prev, language: lang }));
        i18n.changeLanguage(lang);
        flashSaved();
    };

    const handleExportData = async () => {
        setExportingData(true);
        // Simulate gathering data
        await new Promise(r => setTimeout(r, 1200));
        const blob = new Blob(
            [JSON.stringify({
                exportedAt: new Date().toISOString(),
                email: user?.email,
                settings,
                message: 'This is a placeholder export. Full export would include all user data from the backend.',
            }, null, 2)],
            { type: 'application/json' },
        );
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `humanreplica-data-export-${Date.now()}.json`;
        a.click();
        URL.revokeObjectURL(url);
        setExportingData(false);
        setExportDone(true);
        setTimeout(() => setExportDone(false), 3000);
    };

    // ── Inline Toggle Component ──────────────────────────────────────
    const Toggle = ({ on, onToggle }: { on: boolean; onToggle: () => void }) => (
        <button
            onClick={onToggle}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-hig-accent/30 ${on ? 'bg-hig-accent' : 'bg-hig-bg-tertiary'
                }`}
            role="switch"
            aria-checked={on}
        >
            <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform duration-200 ${on ? 'translate-x-6' : 'translate-x-1'
                    }`}
            />
        </button>
    );

    return (
        <div className="max-w-3xl mx-auto space-y-8 pb-12">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-hig-bg-tertiary flex items-center justify-center">
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-hig-text-secondary"><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06A1.65 1.65 0 0 0 19.32 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" /></svg>
                        </div>
                        <div>
                            <h1 className="text-2xl font-bold text-hig-text-primary">Settings</h1>
                            <p className="text-xs text-hig-text-tertiary uppercase tracking-widest font-bold">Infrastructure</p>
                        </div>
                    </div>
                    <p className="text-hig-text-secondary text-sm mt-2">Manage account, integrations, billing, and platform infrastructure</p>
                </div>
                {saved && (
                    <span className="text-xs font-medium text-hig-success bg-hig-success/10 px-3 py-1 rounded-full border border-hig-success/20 animate-fadeIn">
                        ✓ Saved
                    </span>
                )}
            </div>

            {/* Infrastructure Tabs */}
            {/* Infrastructure Tabs */}
            <div className="flex gap-2">
                <button
                    onClick={() => setActiveTab('general')}
                    className={`px-4 py-2 rounded-lg text-sm transition-colors ${
                        activeTab === 'general'
                            ? 'font-semibold bg-hig-accent/10 text-hig-accent border border-hig-accent/20'
                            : 'font-medium text-hig-text-secondary hover:text-hig-text-primary hover:bg-hig-bg-tertiary'
                    }`}
                >
                    General
                </button>
                <button
                    onClick={() => setActiveTab('integrations')}
                    className={`px-4 py-2 rounded-lg text-sm transition-colors ${
                        activeTab === 'integrations'
                            ? 'font-semibold bg-hig-accent/10 text-hig-accent border border-hig-accent/20'
                            : 'font-medium text-hig-text-secondary hover:text-hig-text-primary hover:bg-hig-bg-tertiary'
                    }`}
                >
                    Integrations
                </button>
                <button
                    onClick={() => setActiveTab('billing')}
                    className={`px-4 py-2 rounded-lg text-sm transition-colors ${
                        activeTab === 'billing'
                            ? 'font-semibold bg-hig-accent/10 text-hig-accent border border-hig-accent/20'
                            : 'font-medium text-hig-text-secondary hover:text-hig-text-primary hover:bg-hig-bg-tertiary'
                    }`}
                >
                    Billing
                </button>
                <button
                    onClick={() => setActiveTab('replica-agent')}
                    className={`px-4 py-2 rounded-lg text-sm transition-colors ${
                        activeTab === 'replica-agent'
                            ? 'font-semibold bg-hig-accent/10 text-hig-accent border border-hig-accent/20'
                            : 'font-medium text-hig-text-secondary hover:text-hig-text-primary hover:bg-hig-bg-tertiary'
                    }`}
                >
                    Replica Agent
                </button>
            </div>

            {/* ─── General Tab Content ─────────────────────────────────── */}
            {activeTab === 'general' && (
                <div className="space-y-8">
                    {/* ─── Appearance Section ──────────────────────────────────── */}
                    <div className="card p-6">
                        <h2 className="text-lg font-semibold text-hig-text-primary mb-6">Appearance</h2>

                        <div className="space-y-5">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="font-medium text-hig-text-primary">Theme</p>
                                    <p className="text-sm text-hig-text-secondary">
                                        Choose your preferred color scheme
                                    </p>
                                </div>
                                <ThemeSwitcher showLabels />
                            </div>

                            <div className="flex items-center justify-between pt-4 border-t border-hig-divider">
                                <div>
                                    <p className="font-medium text-hig-text-primary">Language</p>
                                    <p className="text-sm text-hig-text-secondary">
                                        Select your preferred language
                                    </p>
                                </div>
                                <select
                                    value={settings.language}
                                    onChange={(e) => handleLanguageChange(e.target.value)}
                                    className="px-4 py-2 bg-hig-bg-tertiary border border-hig-divider rounded-lg text-sm text-hig-text-primary focus:outline-none focus:border-hig-accent appearance-none cursor-pointer min-w-[140px]"
                                >
                                    <option value="en">English</option>
                                    <option value="fr">Français</option>
                                    <option value="ar">العربية</option>
                                </select>
                            </div>
                        </div>
                    </div>

                    {/* ─── Notifications Section ──────────────────────────────── */}
                    <div className="card p-6">
                        <h2 className="text-lg font-semibold text-hig-text-primary mb-6">Notifications</h2>

                        <div className="space-y-1">
                            <div className="flex items-center justify-between py-3">
                                <div>
                                    <p className="font-medium text-hig-text-primary">Email Notifications</p>
                                    <p className="text-sm text-hig-text-secondary">
                                        Receive updates about your replicas via email
                                    </p>
                                </div>
                                <Toggle on={settings.emailNotifications} onToggle={() => toggle('emailNotifications')} />
                            </div>
                        </div>
                    </div>

                    {/* ─── Account Section ────────────────────────────────────── */}
                    <div className="card p-6">
                        <h2 className="text-lg font-semibold text-hig-text-primary mb-6">Account</h2>

                        <div className="space-y-1">
                            <div className="flex items-center justify-between py-3 border-b border-hig-divider">
                                <div>
                                    <p className="font-medium text-hig-text-primary">Two-Factor Authentication</p>
                                    <p className="text-sm text-hig-text-secondary">
                                        Add an extra layer of security to your account
                                    </p>
                                </div>
                                <button
                                    onClick={() => setShow2FAModal(true)}
                                    className="text-sm text-hig-accent hover:underline font-medium"
                                >
                                    Enable
                                </button>
                            </div>

                            <div className="flex items-center justify-between py-3 border-b border-hig-divider">
                                <div>
                                    <p className="font-medium text-hig-text-primary">Identity Verification</p>
                                    <p className="text-sm text-hig-text-secondary">
                                        Verify your identity for enhanced trust and platform privileges
                                    </p>
                                </div>
                                <button
                                    onClick={() => onNavigate?.('identity-verification')}
                                    className="text-sm text-hig-accent hover:underline font-medium"
                                >
                                    Verify
                                </button>
                            </div>

                            <div className="flex items-center justify-between py-3">
                                <div>
                                    <p className="font-medium text-hig-text-primary">Delete Account</p>
                                    <p className="text-sm text-hig-text-secondary">
                                        Permanently delete your account and all data
                                    </p>
                                </div>
                                <button
                                    onClick={() => setShowDeleteModal(true)}
                                    className="text-sm text-hig-error hover:underline font-medium"
                                >
                                    Delete
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* ─── Privacy Section ────────────────────────────────────── */}
                    <div className="card p-6">
                        <h2 className="text-lg font-semibold text-hig-text-primary mb-6">Privacy</h2>

                        <div className="space-y-1">
                            <div className="flex items-center justify-between py-3 border-b border-hig-divider">
                                <div>
                                    <p className="font-medium text-hig-text-primary">Data Collection</p>
                                    <p className="text-sm text-hig-text-secondary">
                                        Allow anonymous usage analytics to improve the platform
                                    </p>
                                </div>
                                <Toggle on={settings.dataCollection} onToggle={() => toggle('dataCollection')} />
                            </div>

                            <div className="flex items-center justify-between py-3">
                                <div>
                                    <p className="font-medium text-hig-text-primary">Export Data</p>
                                    <p className="text-sm text-hig-text-secondary">
                                        Download all your personal data as a JSON file
                                    </p>
                                </div>
                                <button
                                    onClick={handleExportData}
                                    disabled={exportingData}
                                    className="btn-secondary text-sm py-2 px-4 disabled:opacity-50"
                                >
                                    {exportingData ? 'Preparing...' : exportDone ? '✓ Downloaded' : 'Export'}
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* ─── Webhooks Section ───────────────────────────────────── */}
                    <div className="card p-6">
                        <WebhooksPanel />
                    </div>
                </div>
            )}

            {activeTab === 'integrations' && (
                <IntegrationsCatalogView onNavigate={onNavigate!} hideHeader />
            )}

            {activeTab === 'billing' && (
                <BillingView onNavigate={onNavigate!} hideHeader />
            )}

            {activeTab === 'replica-agent' && (
                <ReplicaAgentTab />
            )}

            {/* ─── 2FA Modal ─────────────────────────────────────────── */}
            <AccessibleModal
                open={show2FAModal}
                onClose={() => setShow2FAModal(false)}
                title="Enable Two-Factor Authentication"
                backdropClassName="bg-black/50 p-4"
                panelClassName="bg-hig-bg-elevated border border-hig-divider rounded-2xl max-w-md w-full p-8 shadow-2xl"
            >
                <h3 className="text-xl font-bold text-hig-text-primary mb-3">Enable Two-Factor Authentication</h3>
                <p className="text-sm text-hig-text-secondary mb-6 leading-relaxed">
                    Two-factor authentication adds an extra layer of security. This feature requires backend integration
                    with an authenticator service (e.g. Google Authenticator, Authy).
                </p>
                <div className="bg-hig-bg-tertiary rounded-xl p-4 mb-6 border border-hig-divider">
                    <p className="text-sm text-hig-text-secondary">
                        <span className="font-semibold text-hig-warning">Coming soon:</span> 2FA setup will be available once the backend authentication service supports TOTP token generation.
                    </p>
                </div>
                <div className="flex items-center justify-end gap-3">
                    <button
                        onClick={() => setShow2FAModal(false)}
                        className="px-5 py-2.5 text-sm text-hig-text-secondary hover:text-hig-text-primary transition-colors rounded-xl"
                    >
                        Close
                    </button>
                </div>
            </AccessibleModal>

            {/* ─── Delete Account Modal ──────────────────────────────── */}
            <AccessibleModal
                open={showDeleteModal}
                onClose={() => setShowDeleteModal(false)}
                title="Delete Account"
                backdropClassName="bg-black/50 p-4"
                panelClassName="bg-hig-bg-elevated border border-hig-divider rounded-2xl max-w-md w-full p-8 shadow-2xl"
            >
                <h3 className="text-xl font-bold text-hig-error mb-3">Delete Account</h3>
                <p className="text-sm text-hig-text-secondary mb-4 leading-relaxed">
                    Are you sure you want to permanently delete your account? This action <strong className="text-hig-text-primary">cannot be undone</strong> and will:
                </p>
                <ul className="text-sm text-hig-text-secondary mb-6 space-y-2 list-disc list-inside">
                    <li>Delete all your Replicas and training data</li>
                    <li>Remove all team memberships</li>
                    <li>Cancel any active billing subscriptions</li>
                    <li>Erase all conversation history</li>
                </ul>
                <div className="flex items-center justify-end gap-3">
                    <button
                        onClick={() => setShowDeleteModal(false)}
                        className="px-5 py-2.5 text-sm text-hig-text-secondary hover:text-hig-text-primary transition-colors rounded-xl"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={() => {
                            alert('Account deletion requires backend implementation. Contact support for manual deletion.');
                            setShowDeleteModal(false);
                        }}
                        className="px-5 py-2.5 bg-hig-error text-white rounded-xl text-sm font-medium hover:bg-hig-error/90 transition-colors"
                    >
                        Delete My Account
                    </button>
                </div>
            </AccessibleModal>
        </div>
    );
}
